<template>
  <wa-page
    view-slug="icon-landing"
    :is-showing-v7-prerelease-notice="true"
  >
    <wa-page-header slot="header" />

    <div
      slot="content"
      class="app-content"
      role="main"
    >
      <wa-icons-loading v-if="isLoading" />
      <ais-instant-search
        v-else
        :search-client="searchClient"
        :index-name="index"
      >
        <ais-configure
          :distinct="true"
          :facet-filters.camel="facetFilters()"
        />

        <ais-hits
          v-if="iconId != null"
          :transform-items="transformItems"
          class="app-content"
          role="main"
        >
          <div
            v-if="items.length > 0"
            slot="default"
            slot-scope="{ items }"
          >
            <div id="icon-landing">
              <main
                class="padding-y-5xl"
                style="background: var(--white)"
              >
                <div class="container">
                  <template v-if="display == 'detail'">
                    <wa-old-version-alert
                      v-if="isOlderVersion"
                      :subject="icon.name"
                      is-individual-icon
                    />
                    <div class="row roomy laptop:align-stretch laptop:align-between margin-bottom-lg">
                      <div class="column-12 laptop:column-6">
                        <icon-name-and-link
                          :icon="icon"
                          :is-pro-user="isProUser"
                          :is-pro-icon="isProIcon"
                          :is-using-modal="false"
                          :styling="styling"
                          @openProIconModal="openProIconModal"
                        />
                      </div>

                      <div
                        class="column-12 laptop:column-6 display-flex flex-row flex-nowrap flex-items-center laptop:flex-content-end"
                        style="gap: var(--spacing-md)"
                      >
                        <icon-unicodes
                          :icon="icon"
                          :is-duotone="isDuotoneIcon"
                          :is-using-modal="false"
                          :styling="styling"
                        />
                        <icon-glyphs
                          :icon="icon"
                          :is-duotone="isDuotoneIcon"
                          :is-using-modal="false"
                          :styling="styling"
                        />
                        <icon-svg-download
                          :can-download-icon="canDownloadIcon"
                          :icon="icon"
                          :is-pro-icon="isProIcon"
                          :is-pro-user="isProUser"
                          :is-custom-icon="false"
                          :is-using-modal="false"
                          :styling="styling"
                          @openProIconModal="openProIconModal"
                          @getSelectedAction="getSelectedAction"
                        />
                      </div>
                    </div>

                    <div
                      v-if="$marketing.showAdvertising"
                      class="row roomy align-stretch margin-bottom-2xl"
                    >
                      <div class="column-12 laptop:column">
                        <wa-carbon-ad />
                      </div>
                    </div>

                    <div class="wrap-icon-details row roomy align-stretch laptop:margin-bottom-2xl">
                      <div class="icon-detail-grid-preview column-12 margin-bottom-2xl laptop:margin-bottom-0 laptop:column-6">
                        <icon-preview
                          :preview-mode="previewMode"
                          :styling="styling"
                          :icon="icon"
                          :default-styles="defaultStyles"
                          :display-icon-preview-controls="true"
                          :version="version"
                          :is-duotone="isDuotoneIcon"
                          @resetStyles="resetStyles"
                          @changePreviewMode="changePreviewMode"
                          @toggleStyleOptions="toggleStyleOptions"
                          @toggleAnimationOptions="toggleAnimationOptions"
                        />
                      </div>

                      <div class="icon-detail-use column-12 laptop:column-6 margin-bottom-2xl laptop:margin-bottom-0">
                        <div class="margin-bottom-2xl">
                          <icon-pack-selector
                            :icon="icon"
                            :styling="styling"
                            :version="version"
                            :default-styles="defaultStyles"
                            @change="(newStyling) => (styling = newStyling)"
                          />
                        </div>

                        <icon-code-snippet
                          :id="icon.name"
                          :can-download-icon="canDownloadIcon"
                          :default-styles="defaultStyles"
                          :icon="icon"
                          :icon-abstract="iconAbstract"
                          :is-duotone="isDuotoneIcon"
                          :is-pro-icon="isProIcon"
                          :is-pro-user="isProUser"
                          :is-using-modal="false"
                          :showing="showing"
                          :styling="styling"
                          :viggo="viggo"
                          @show="changeShowing"
                          @openProIconModal="openProIconModal"
                          @getSelectedAction="getSelectedAction"
                        />

                        <icon-actions
                          :can-access-icon="canAccessIcon"
                          :can-use-icon="canUseIcon"
                          :icon="icon"
                          :is-custom-icon="false"
                          :is-pro-icon="isProIcon"
                          :is-pro-plus-icon="isProPlusIcon(icon)"
                          :is-pro-user="isProUser"
                          :is-using-modal="false"
                          :styling="styling"
                          @editIcon="editIcon"
                          @getSelectedAction="getSelectedAction"
                          @openProIconModal="openProIconModal"
                        />
                      </div>

                      <div class="icon-detail-styling-tabs column-12">
                        <icon-styling
                          v-show="isShowingStyleOptions"
                          :icon="icon"
                          :styling="styling"
                          :version="version"
                          :default-styles="defaultStyles"
                          @change="(newStyling) => (styling = newStyling)"
                        />

                        <icon-animations
                          v-show="isShowingAnimationOptions"
                          :icon="icon"
                          :styling="styling"
                          :version="version"
                          @change="(newStyling) => (styling = newStyling)"
                        />
                      </div>
                    </div>

                    <icon-brand-notice v-if="isBrandsIcon" />

                    <icon-sponsorship
                      v-if="isSponsored"
                      :sponsor-name="icon.sponsored.label"
                      :sponsor-url="icon.sponsored.url"
                    />

                    <div
                      class="row roomy align-middle align-center"
                      style="gap: var(--spacing-md)"
                    >
                      <icon-categories
                        v-if="hasCategories"
                        :categories="icon.categories"
                        :icon="icon"
                        :is-using-modal="true"
                      />
                      <icon-aliases
                        v-if="hasAliases"
                        :aliases="icon.aliases.names"
                      />

                      <icon-version-info :icon="icon" />
                    </div>
                  </template>

                  <div v-if="display == 'edit'">
                    <icon-customize
                      :default-styles="defaultStyles"
                      :icon="icon"
                      :is-pro-user="isProUser"
                      :is-using-modal="false"
                      :selected-family="styling.family"
                      :selected-style="styling.style"
                      :styling="styling"
                      :version="version"
                      @closeEditor="closeEditor"
                      @change="(newStyling) => (styling = newStyling)"
                    />
                  </div>
                </div>
              </main>

              <aside class="container padding-top-5xl padding-bottom-7xl">
                <icon-examples
                  :icon="icon"
                  :selected-family="styling.family"
                />

                <more-icons-like
                  :icon="icon"
                  :results-view="resultsView"
                  :selected-style="styling.style"
                  :selected-family="styling.family"
                  :search-query="icon.name"
                  :version="version"
                />
              </aside>

              <wa-modal-upsell
                :icon="icon"
                :is-showing="isShowingUpsellModal"
                :upsell-requirement="upsellRequirement"
                :upsell-trigger="getSelectedAction(selectedAction)"
                :upsell-trigger-icon-name="selectedActionIcon"
                @close-modal="closeProIconModal"
              />
            </div>
          </div>

          <icon-does-not-exist
            v-else
            slot="content"
            class="app-content"
            :icon-name="iconId"
            :selected-style="styling.style"
            :selected-family="styling.family"
            :version="version"
          />
        </ais-hits>
      </ais-instant-search>
    </div>

    <wa-page-footer slot="footer" />
  </wa-page>
</template>

<script>
  import { mapGetters } from 'vuex'
  import algoliasearch from 'algoliasearch/lite'
  import { get, isEmpty, isEqual, merge, pick, size, startCase } from 'lodash'

  import IconSvgDownload from 'app/views/icons/components/icons/details/IconSvgDownload'
  import IconActions from './components/icons/details/IconActions'
  import IconAliases from './components/icons/details/IconAliases'
  import IconAnimations from './components/icons/details/IconAnimations'
  import IconBrandNotice from './components/icons/details/IconBrandNotice'
  import IconCategories from './components/icons/details/IconCategories'
  import IconCodeSnippet from './components/icons/details/IconCodeSnippet'
  import IconCustomize from './components/icons/modifiers/IconCustomize'
  import IconDoesNotExist from './components/icons/details/IconDoesNotExist'
  import IconExamples from './components/icons/details/IconExamples'
  import IconPackSelector from './components/icons/details/IconPackSelector'
  import IconGlyphs from './components/icons/details/IconGlyphs'
  import IconNameAndLink from './components/icons/details/IconNameAndLink'
  import IconPreview from './components/icons/details/IconPreview'
  import IconSponsorship from './components/icons/details/IconSponsorship'
  import IconStyling from './components/icons/details/IconStyling'
  import IconUnicodes from './components/icons/details/IconUnicodes'
  import IconVersionInfo from './components/icons/details/IconVersionInfo'
  import MoreIconsLike from './components/icons/MoreIconsLike'

  import { DEFAULT_STYLING } from 'app/views/icons/defaults'
  import { EventTrackingMixin, hasAccessToProForActiveVersion, SubscriptionMixin } from 'app/mixins'
  import { fetchIndexName } from 'app/views/search/utils'
  import { fetchSvg } from 'app/views/icons/components/icons/details/utils'
  import { IconsMixins } from 'app/views/icons/components/icons/mixins'
  import { loadViggo } from 'app/viggo'
  import { stylingToQuery } from './routing'

  export default {
    name: 'IconLanding',

    components: {
      IconActions,
      IconAliases,
      IconAnimations,
      IconBrandNotice,
      IconCategories,
      IconCodeSnippet,
      IconCustomize,
      IconDoesNotExist,
      IconExamples,
      IconPackSelector,
      IconGlyphs,
      IconNameAndLink,
      IconPreview,
      IconSponsorship,
      IconStyling,
      IconSvgDownload,
      IconUnicodes,
      IconVersionInfo,
      MoreIconsLike
    },

    mixins: [EventTrackingMixin, hasAccessToProForActiveVersion, IconsMixins, SubscriptionMixin],

    beforeRouteEnter(to, _from, next) {
      if (0 === size(get(to, ['query', 's']))) {
        next({ ...to, query: { ...to.query, s: 'solid' }, replace: true })
      } else {
        next()
      }
    },

    data() {
      return {
        previewMode: 'day',
        display: 'detail',
        doesNotExist: false,
        icon: { membership: { free: [], pro: [] }, aliases: {}, styles: [], sponsored: {}, changes: [] },
        iconAbstract: null,
        isLoading: false,
        resultsView: 'compact',
        searchClient: algoliasearch(FONTAWESOME.ALGOLIA_APPLICATION_ID, FONTAWESOME.ALGOLIA_SEARCH_API_KEY),
        showing: 'html',
        isShowingUpsellModal: false,
        isShowingStyleOptions: false,
        isShowingAnimationOptions: false,
        styling: {
          animation: this.$route.query.an ? this.$route.query.an : DEFAULT_STYLING.animation,
          colorPrimary: this.$route.query.pc ? this.$route.query.pc : DEFAULT_STYLING.colorPrimary,
          colorSecondary: this.$route.query.sc ? this.$route.query.sc : DEFAULT_STYLING.colorSecondary,
          family: this.$route.query.f || 'classic',
          opacityPrimary: this.$route.query.po ? this.$route.query.po : DEFAULT_STYLING.opacityPrimary,
          opacitySecondary: this.$route.query.so ? this.$route.query.so : DEFAULT_STYLING.opacitySecondary,
          rotation: this.$route.query.rt ? this.$route.query.rt : DEFAULT_STYLING.rotation,
          size: this.$route.query.sz ? this.$route.query.sz : DEFAULT_STYLING.size,
          style: this.$route.query.s || 'solid'
        },
        viggo: null,
        selectedAction: 'Whoopsie! That action'
      }
    },

    computed: {
      ...mapGetters('plans', ['iconIsIncludedInPlan', 'isProPlusIcon', 'canAccessIconSvg']),

      defaultStyles() {
        const stylingDefaults = { ...DEFAULT_STYLING }

        stylingDefaults.family = this.styling.family
        stylingDefaults.style = this.styling.style

        return stylingDefaults
      },

      isCustomIcon() {
        // Icon Landing doesn't handle custom icons, so return false
        return false
      },

      index() {
        return fetchIndexName(this.$releases.active.version)
      },

      isOlderVersion() {
        return this.$releases.active.versionParts.major === 5 ? true : false
      },

      hasAliases() {
        return this.icon.aliases.names && this.icon.aliases.names.length > 0
      },

      hasCategories() {
        return this.icon.categories.length > 0
      },

      isBrandsIcon() {
        return this.icon.family === 'brands'
      },

      isProUser() {
        return this.hasAccessToProForActiveVersion
      },

      isProIcon() {
        return !this.icon.is_free
      },

      isSponsored() {
        return this.icon.sponsored.label && this.icon.sponsored.label.length > 0
      },

      iconId() {
        return this.$route.path.split('/').pop()
      },

      objectID() {
        const family = get(this, ['styling', 'family'], 'classic') // default to classic if not set
        const style = get(this, ['styling', 'style'], 'solid') // default to solid if not set

        return `i:${family}:${style}:${this.iconId}`
      },

      pageTitle() {
        if (this.styling.style === 'solid') {
          return `${startCase(this.$route.params.icon)} Icon`
        } else {
          return `${startCase(this.$route.params.icon)} Icon - ${startCase(this.styling.style)}`
        }
      },

      version() {
        return this.$releases.active.versionParts.major
      },

      userHasActiveProSubscription() {
        return this.isProUser && this.$permits.has('accessToProDownload')
      },

      upsellRequirement() {
        return this.isProPlusIcon(this.icon) ? 'Pro+ Plan' : 'Pro Plan'
      },

      isIconInPlan() {
        const planSku = this.$subscription ? this.$subscription.plan.sku : 'FA-FREE'

        return this.iconIsIncludedInPlan(this.icon, planSku)
      },

      earlyBackerWithoutSubscription() {
        return this.$dt.isBacker && this.$subscription === null
      },

      earlyBackerHasIconAccess() {
        return this.earlyBackerWithoutSubscription && this.hasAccessToProForActiveVersion
      },

      canDownloadIcon() {
        if (this.earlyBackerHasIconAccess) return true

        if (!this.$dt.active && !this.icon.is_free) return false

        if (!this.isIconInPlan) return false

        const accessToSvg = this.canAccessIconSvg(this.icon, this.$subscription?.plan, this.userHasActiveProSubscription)

        return accessToSvg
      },

      canUseIcon() {
        if (this.earlyBackerHasIconAccess) return true

        if (this.isCustomIcon) return true
        if (this.icon.is_free) return true

        const activeProSubscription = this.$dt.active && this.$subscription.plan.sku !== 'FA-FREE'

        return this.isIconInPlan && activeProSubscription
      },

      canAccessIcon() {
        return this.canDownloadIcon && this.canUseIcon
      },

      selectedActionIcon() {
        if (this.selectedAction.toLowerCase().includes('downloading')) {
          return `faArrowDownToLine`
        }

        return `faIcons`
      }
    },

    watch: {
      styling(newValue, oldValue) {
        if (!isEqual(newValue, oldValue)) {
          this.updateRoute()
        }
      },

      icon(newValue, oldValue) {
        const overrides = {}

        if (this.showing === 'svg' && !this.canAccessIcon) {
          this.showing = 'html'
        }

        if (!this.styling.family && newValue.family) {
          overrides.family = newValue.family
        }

        if (newValue.family === 'brands' && newValue.family !== this.styling.family) {
          overrides.family = 'brands'
          overrides.style = 'solid'
        }

        if (!isEmpty(overrides)) {
          this.styling = { ...this.styling, ...overrides }
        }

        /*
         * watching and checking to see if the family or style has changed
         * if changed, we need to fetch the new svg
         * fetchSvg(), in this instance, is ultimately used to get the iconAbstract
         * and pass along to the codeSnippetSvg.vue component
         */
        if (newValue.family !== oldValue.family || newValue.style !== oldValue.style) {
          fetchSvg(this.viggo, this.isCustomIcon, this.icon, this.$releases.active.version, this.styling)
            .then((iconAbstract) => {
              this.iconAbstract = iconAbstract
            })
            .catch((error) => {
              throw new Error('fetchSvg() in watcher', error)
            })
        }
      }
    },

    async created() {
      // Handle old urls with querystring: ?f=classic&s=duotone
      if (!this.isOlderVersion && this.styling.family === 'classic' && this.styling.style === 'duotone') {
        this.styling.family = 'duotone'
        this.styling.style = 'solid'
      }

      if (['sz', 'pc', 'rt', 'an'].some((q) => Object.keys(this.$route.query).includes(q))) {
        this.isShowingStyleOptions = true
      }

      if (!this.viggo) {
        try {
          this.viggo = await loadViggo()
        } catch (error) {
          throw new Error('Error loading viggo', error)
        }
      }

      if (this.icon.name) {
        try {
          this.iconAbstract = await fetchSvg(this.viggo, this.isCustomIcon, this.icon, this.$releases.active.version, this.styling)
        } catch (error) {
          throw new Error('fetchingSvg()', error)
        }
      }
    },

    beforeMount() {
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
      }, 300)
    },

    mounted() {
      this.$posthog.capture(
        'icon_view',
        merge({ context: 'icon_landing', icon_name: this.icon.name, icon_style: this.icon.style, icon_family: this.icon.family }, this.determinationsEventProps)
      )
    },

    methods: {
      getSelectedAction(action) {
        this.selectedAction = action

        return this.selectedAction
      },

      changePreviewMode(mode) {
        return (this.previewMode = mode)
      },

      transformItems(items) {
        if (items.length >= 1) {
          this.icon = items[0]
        }

        return items
      },

      changeShowing(snippet) {
        if (snippet === 'svg' && !this.canAccessIcon) {
          this.selectedAction = `Downloading this Icon`
          return this.openProIconModal()
        } else {
          this.showing = snippet
        }
      },

      openProIconModal() {
        const skipModal = this.$posthog.getFeatureFlag('skip-pro-modal-go-to-plans-pro') === 'skip-modal'

        this.purchaseFlowEvent('upsell_click')

        if (skipModal) {
          this.$router.push({ name: 'plans.pro' })
        } else {
          this.purchaseFlowEvent('pro_icon_modal_view')
          this.isShowingUpsellModal = true
        }
      },

      closeProIconModal() {
        this.isShowingUpsellModal = false
      },

      facetFilters() {
        // Find by icon name OR alias using Algolia's AND/OR syntax. See the following doc for explanation:
        // https://www.algolia.com/doc/guides/managing-results/refine-results/filtering/in-depth/filters-and-facetfilters/#syntax-difference-between-filters-and-facetfilters
        // Brands was added so brand icons will load without "?f=brands" query string
        return [
          [
            // search for the objectID OR alias name
            `objectID:${this.objectID}`,
            `objectID:i:brands:${this.iconId}`,
            `aliases.names:${this.iconId}`
          ]

          // [ //AND style
          //   `style:${this.styling.style}`,
          //   `style:brands`
          // ]
        ]
      },

      resetStyles() {
        this.styling = { ...this.styling, ...DEFAULT_STYLING }
      },

      stylingChange(styling) {
        this.styling = { ...this.styling, ...styling }
      },

      toggleStyleOptions() {
        this.isShowingAnimationOptions = false
        this.isShowingStyleOptions = !this.isShowingStyleOptions
      },

      toggleAnimationOptions() {
        this.isShowingStyleOptions = false
        this.isShowingAnimationOptions = !this.isShowingAnimationOptions
      },

      updateRoute() {
        const route = pick({ ...this.$route }, ['name', 'params', 'query'])

        route.query = stylingToQuery(this.styling)

        this.$router.replace(route)
      },

      editIcon() {
        this.display = 'edit'
      },

      closeEditor() {
        this.display = 'detail'
      }
    }
  }
</script>
